import { FC, PropsWithChildren, useCallback, useEffect, useMemo, useRef, useState } from 'react'
import { Input, Empty } from 'antd'
import { SearchOutlined, MenuFoldOutlined, MenuUnfoldOutlined } from '@ant-design/icons'
import MenuBlock from './MenuBlock'
import * as api from '@/api/user'
import { stopPropagation } from '@/utils/common'
import styles from '../style.module.less'
import icon from '@/assets/images/temp/icon1.png'

// 标记菜单层级
const layerTag = (menu) => {
  let deep = 1
  if(menu.children && menu.children.length > 0) {
    const layers = menu.children.map(c => layerTag(c))
    deep = deep + Math.max(...layers)
  }
  menu.deep = deep
  menu.collapsedName = menu.name.substr(0, 4)
  if(!menu.icon) {
    menu.icon = icon
  }
  return deep
}

const menuSort = (menus, dir) => {
  menus.forEach(menu => {
    if(menu.children) {
      menu.children.sort((a, b) => {
        if(a.deep > b.deep) {
          return dir
        }
        if(a.deep < b.deep) {
          return -dir
        }
        return 0
      })
      menuSort(menu.children, -1)
    }
  })
}

// 获取默认页地址
const getDefaultPage = (menus, rootMenu?) => {
  for(let i=0; i<menus.length; i++) {
    if(menus[i].deep === 1) {
      return {
        menu: menus[i],
        rootMenu: rootMenu || menus[i]
      }
    }
    if(menus[i].children) {
      const isChildrenPage = getDefaultPage(menus[i].children, rootMenu || menus[i])
      if(isChildrenPage) {
        return isChildrenPage
      }
    }
  }
}

type PropType = FC<PropsWithChildren<{
  onChange: (menu) => void
  onMenuDataChange: (menus) => void
}>>

const filterTree = (data, keyword) => {
  if (!Array.isArray(data)) return [];

  return data.reduce((acc, node) => {

    // 先递归处理子节点
    const filteredChildren = filterTree(node.children, keyword);

    // 当前节点是否匹配关键字
    const matches = node.name.includes(keyword);

    // 如果匹配 或者 子节点有匹配项，则保留当前节点，并更新其子节点为过滤后的结果
    if (matches || filteredChildren.length > 0) {
      acc.push({
        ...node,
        children: filteredChildren
      });
    }

    return acc;
  }, []);
}

const MainMenuFilter = ({menus, onChange}) => {
  const [keyword, setKeyword] = useState('')
  const [visible, setVisible] = useState(false)
  const inputRef = useRef()

  const handleKeywordChange = useCallback((e) => {
    setKeyword(e.target.value)
  }, [])

  const singleMenus = useMemo(() => {
    const res: {name: string, path: string}[] = []
    const joinName = (menus, parent) => {
      for(let i=0; i<menus.length; i++) {
        const targetName = parent + '/' + menus[i].name
        if(menus[i].children && menus[i].children.length > 0) {
          joinName(menus[i].children, targetName)
        } else {
          res.push({
            name: targetName,
            path: menus[i].path
          })
        }
      }
    }
    joinName(menus, '')
    return res
  }, [menus])

  const filterRes = useMemo(() => {
    if(!keyword) {
      return []
    }
    return singleMenus.filter(r => r.name.includes(keyword))
  }, [singleMenus, keyword])

  const handleMenuClick = useCallback((menu) => {
    if(typeof onChange === 'function') {
      onChange(menu)
      setVisible(false)
    }
  }, [onChange])

  const handleGlobalClick = useCallback((e) => {
    e.stopPropagation()
    
  }, [])

  useEffect(() => {
    const globalClick = () => {
      setVisible(false)
    }
    window.addEventListener('click', globalClick)
    return () => {
      window.removeEventListener('click', globalClick)
    }
  }, [])

  const handleCheckVisible = useCallback(() => {
    setVisible(true)
  }, [])

  const handleClose = useCallback(() => {
    setVisible(false)
  }, [])

  useEffect(() => {
    if(!visible) {
      setKeyword('')
    }
  }, [visible])

  const handleEmptyClick = useCallback(() => {
    const ipt = inputRef.current as any
    ipt.focus()
  }, [])

  return (
    <div className={styles['main-menu-search']} onClick={handleGlobalClick}>
      <div className={styles['main-menu-search-block']} onClick={handleCheckVisible}>
        <Input allowClear value={keyword} onChange={handleKeywordChange} ref={inputRef} />
        {
          !visible && (
            <div className={styles['main-menu-search-btn']}>
              <SearchOutlined />
              <span className='font-14'>搜索菜单</span>
            </div>
          )
        }
      </div>
      {
        visible && (
          <>
            <div className={styles['main-menu-search-cover']} onClick={handleClose}></div>
            <div className={styles['main-menu-search-container']}>
              <div className={styles['main-menu-search-result-container']}>
                {
                  filterRes.map(r => (
                    <div key={r.name} className={styles['main-menu-search-result']} onClick={() => handleMenuClick(r)}>{r.name}</div>
                  ))
                }
                {
                  filterRes.length === 0 && (
                    <div className='ptb-20' onClick={handleEmptyClick}>
                      <Empty image={Empty.PRESENTED_IMAGE_SIMPLE} description="请输入您想要搜索的菜单关键字查询" />
                    </div>
                  )
                }
              </div>
            </div>
          </>
        )
      }
      
    </div>
  )
}

const Menu: PropType = ({onChange, onMenuDataChange}) => {
  const [menus, setMenus] = useState<any[]>([])
  const [activeMenu, setActiveMenu] = useState('')
  const [open, setOpen] = useState(false)
  const wrapRef = useRef<HTMLDivElement>()
  const [filterKey, setFilterKey] = useState('')
  const [collapsed, setCollapsed] = useState(false)

  // 获取菜单数据
  const getMenuData = useCallback(async() => {
    const res = await api.getMenuData()
    if(res) {
      onMenuDataChange(res)
      res.forEach(m => layerTag(m))
      // 菜单数据排序
      menuSort(res, 1)
      setMenus(res)
      return
      // 默认打开第一个页面
      const defaultPage = getDefaultPage(res)
      if(!activeMenu && defaultPage) {
        setActiveMenu(defaultPage.rootMenu.hmid)
        onChange(defaultPage.menu)
      }
    }
  }, [activeMenu, onMenuDataChange, onChange])

  useEffect(() => {
    getMenuData()
  }, [getMenuData])

  const activeMenuObj = useMemo(() => {
    const target = menus.find(m => m.hmid === activeMenu)
    if(!target) {
      return {
        name: '',
        children: []
      }
    }
    let children = target.children
    if(filterKey) {
      children = filterTree(children, filterKey)
    }
    return {
      name: target.name,
      children
    }
  }, [activeMenu, menus, filterKey])

  // 选择一级菜单
  const handleFirstMenu = useCallback((menu) => {
    setFilterKey('')
    setActiveMenu(menu.hmid)
    if(menu.deep === 1) {
      onChange(menu)
      setOpen(false)
    } else {
      setOpen(true)
    }
  }, [onChange])

  // 选择子菜单
  const handleMenuBlock = useCallback((menu) => {
    onChange(menu)
    setOpen(false)
  }, [onChange])

  const handleCoverClick = useCallback(() => {
    setOpen(false)
  }, [])

  const handleSearch = useCallback((e) => {
    setFilterKey(e.target.value)
  }, [])

  const handleCollapsed = useCallback(() => {
    setCollapsed(!collapsed)
  }, [collapsed])

  return (
    <div className={`${styles.menu} ${collapsed ? styles.collapsed : ''}`} ref={wrapRef}>
      <div className={styles['menu-item-container']}>
        <div className='flex items-center relative border-bottom'>
          {
            !collapsed && <MainMenuFilter menus={menus} onChange={onChange} />
          }
          <div className={styles['menu-collapsed']} onClick={handleCollapsed}>
            {
              collapsed ? <MenuUnfoldOutlined /> : <MenuFoldOutlined />
            }
          </div>
          
        </div>
        <div className={styles['menu-main']}>
          {
            menus.map(menu => (
              <div title={collapsed ? menu.name : ''} className={`${styles['menu-item']} ${styles['left-menu-item']} ${activeMenu === menu.hmid ? styles.active : ''}`} key={menu.hmid} onClick={() => handleFirstMenu(menu)}>
                <img src={menu.icon} alt="" />
                <div className={`flex-1 w-0 ellipsis ${styles['menu-item-label']}`}>{ collapsed ? menu.collapsedName : menu.name}</div>
              </div>
            ))
          }
        </div>
        
      </div>
      {
        open &&  <div className={styles['menu-container']} onClick={handleCoverClick}>
          <div className={`h-full flex flex-column ${styles['menu-content']}`} onClick={stopPropagation}>
            <div className='border-bottom flex align-center plr-10 ptb-10 bg-white'>
              <div className='flex-1 font-16 font-700'>{activeMenuObj.name}</div>
              <Input allowClear placeholder="搜索" prefix={<SearchOutlined />} value={filterKey} className={styles['menu-search']} onChange={handleSearch} />
            </div>
            <div className='flex-1 h-0 overflow-auto flex flex-wrap align-start p-6 align-content-start'>
              {
                activeMenuObj.children.map(item => <MenuBlock onChange={handleMenuBlock} key={item.hmid} data={item} />)
              }
              {
                filterKey && activeMenuObj.children.length === 0 && <div className='flex-1 mt-80'><Empty description="没有搜索到菜单，请重新输入" /></div>
              }
            </div>
          </div>
        </div>
      }
     
    </div>
  )
}

export default Menu
